/**
 * apps-adapter.ts — 真实 API DTO（AppResponse, snake_case）→ 设计层富集行（AppRow, camelCase）塑形
 *
 * 「接真」核心：页面与设计 100% 不动，只把数据从 mock 换成真实后端。
 * AppResponse（packages/shared/src/schemas/apps.ts）是规范化的 snake_case DTO，
 * 但设计稿用的 AppRow（~/data/mock/types.ts）是 camelCase 富集行，带 mock 才有的聚合/派生字段。
 * 本文件负责字段映射 + 缺字段补齐，保证 AppRow 形状 100% 还原。
 *
 * 字段映射（AppResponse → AppRow）：
 *   id              → id（透传）
 *   name            → name（透传）
 *   app_code        → appCode（透传；未配置 / 旧 Worker 未返回 → null，UI 渲染 '—'）
 *   platform        → platform（IOS→'iOS' / ANDROID→'Android'）
 *   name + id       → icon（name 前 2 字母大写）/ color（id+name hash 配色）
 *   region_codes      → market（App 运营市场多选集合，直接透传为多市场数组；替代旧单值 country）
 *   allowed_languages → lang（App 允许语言集合，直接透传为多语言数组；替代旧单值 locale）
 *   —（真 API 已删 market_timezone）→ tz（App 层时区字段已废弃 → 给固定默认值，见下）
 *   is_active       → status（true→'active' / false→'paused'）
 *   book_count        → books（当前实际可见的去重书组数）
 *   —（真 API 无）   → users / dau / revenue / refundRate：经 opts 注入，缺省 undefined
 *
 * 缺字段补齐策略：真实 API 普遍缺 mock 的聚合/关联统计。这些字段语义上属于「别的域」
 * （users 域 / finance 域），调用方按需经 opts 注入；未注入则为 undefined（前端渲染 '—'），不落 0 冒充真值。
 */

import type { AppResponse } from '@novel-admin/shared';
import type { AppRow } from '~/data/mock/types';

/**
 * 经 opts 注入的聚合/关联统计字段 —— 真实 apps DTO 不含，调用方按需拉取后传入。
 * 全部 optional：未拉取时各自为 undefined（见 adaptAppToRow，UI 渲染 '—'），不编造数据。
 *  - users      用户域 stats（注册用户数）
 *  - dau        用户域 stats（日活）
 *  - revenue    财务域 stats（营收，已折算展示币种数值）
 *  - refundRate 财务域 stats（退款率，0~1 或百分比，按 finance stats 端点口径）
 */
export interface AppRowAggregates {
  users?: number;
  dau?: number;
  revenue?: number;
  refundRate?: number;
}

/**
 * 由 name 派生 2 字母大写图标占位（mock 同款：取前两个「单词首字母」，不足则取前两个字母）。
 * 例：'NovelOcean' → 'NO'（驼峰拆词）/ 'LoveReads' → 'LR' / 'sora' → 'SO' / 'X' → 'X'。
 * 拆词规则：先按非字母分隔 + 驼峰边界切词取各词首字母；切不出两词则退化为「前两个字母」。
 */
function deriveIcon(name: string): string {
  const words = name
    // 驼峰边界插空格：NovelOcean → Novel Ocean
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // 非字母数字（空格 / 连字符 / 下划线 等）统一作分隔
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w.length > 0);
  if (words.length >= 2) {
    const a = words[0]?.[0] ?? '';
    const b = words[1]?.[0] ?? '';
    const initials = `${a}${b}`.toUpperCase();
    if (initials.length === 2) return initials;
  }
  // 单词：取前两个字母（仅字母数字）
  const letters = name.replace(/[^A-Za-z0-9]+/g, '');
  return (letters.slice(0, 2) || '?').toUpperCase();
}

/** 调色板 —— 与设计 token 的 --chart-* 同色系档位（数据字段，非 CSS，存的是 swatch hex）。 */
const ICON_PALETTE = [
  '#E07A4D',
  '#6B7BD9',
  '#2F8F6B',
  '#B5527A',
  '#3A7BB5',
  '#C4824A',
  '#7A6BD9',
  '#4D9E8F',
] as const;

/**
 * 由 id（+ name 兜底）稳定 hash 选一个配色档位，保证同一 App 永远同色、跨刷新不变。
 * djb2 hash：纯函数、无依赖、分布均匀，足够给固定数量 App 上色。
 */
function deriveColor(id: string, name: string): string {
  const seed = id || name;
  let hash = 5381;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 33 + seed.charCodeAt(i)) >>> 0;
  }
  const index = hash % ICON_PALETTE.length;
  // 字面量数组 + 取模索引：index 必在界内，但 noUncheckedIndexedAccess 下仍兜底首档。
  return ICON_PALETTE[index] ?? ICON_PALETTE[0];
}

/**
 * adaptAppToRow — 把单个真实 AppResponse 塑形成设计层 AppRow。
 *
 * 纯函数：platform 映射自 DTO canonical 字段；其余派生字段（icon / color / market / lang / tz / status）由 DTO 推导，
 * books 直接取 AppResponse.book_count；其余聚合字段（users / dau / revenue / refundRate）由 opts 注入、
 * 缺省 undefined（渲染 '—'）。调用方无需为 books 发起 N+1 stats 请求。
 */
export function adaptAppToRow(app: AppResponse, opts?: AppRowAggregates): AppRow {
  return {
    id: app.id,
    name: app.name,
    // ADM-APP-S019：`?? null` 兜的是 Web 先于 Server 部署的窗口（旧 Worker 不返该键）
    appCode: app.app_code ?? null,
    // 激励广告四项默认参数整份透传，不按字段拼接语言覆盖。
    rewardedAdConfig: app.rewarded_ad_config,
    platform: app.platform === 'IOS' ? 'iOS' : app.platform === 'ANDROID' ? 'Android' : 'WEB',
    icon: deriveIcon(app.name),
    color: deriveColor(app.id, app.name),
    // 真 API region_codes 已是多市场数组（替代旧单值 country）→ 直接透传；lang 用 allowed_languages 多语言集合
    market: app.region_codes,
    lang: app.allowed_languages,
    // App 层 market_timezone 已废弃（日切时区下沉到 users.day_cut_timezone，admin 不再交互）。
    // AppRow.tz 是非空 string，给固定占位默认值，仅用于设计层展示，不参与任何业务计算。
    tz: 'Europe/Berlin',
    status: app.is_active ? 'active' : 'paused',
    // users / dau / revenue / refundRate 经 opts 注入；未注入 = undefined → 前端渲染 '—'。
    users: opts?.users,
    dau: opts?.dau,
    revenue: opts?.revenue,
    books: app.book_count,
    refundRate: opts?.refundRate,
  };
}
